import type {
    LogicActionNode,
    LogicConditionNode,
    LogicGroupNode,
    LogicReferenceKind,
    LogicReferenceNode,
    LogicScheduleNode,
    LogicSequenceNode,
    TradeLogicComparisonOperator,
    TradeLogicContextFeatureRef,
    TradeLogicJsonObject,
    TradeLogicJsonValue,
    TradeLogicMissingDataPolicy,
    TradeLogicNodeId,
    TradeLogicRegistryKey
} from './types';

export type TradeLogicNodeIdFactory = (prefix: string) => TradeLogicNodeId;

const defaultNodeIdFactory: TradeLogicNodeIdFactory = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export interface CreateLogicNodeFactoryOptions {
    createId?: TradeLogicNodeIdFactory;
}

const nextId = (prefix: string, options: CreateLogicNodeFactoryOptions = {}) => (options.createId ?? defaultNodeIdFactory)(prefix);

export const createLogicRootGroup = (options: CreateLogicNodeFactoryOptions = {}): LogicGroupNode => ({
    id: nextId('group', options),
    kind: 'group',
    operator: 'all',
    label: 'Root group',
    enabled: true,
    children: []
});

export const createLogicGroup = (
    params: Partial<Pick<LogicGroupNode, 'label' | 'operator' | 'children' | 'threshold' | 'weightThreshold'>> = {},
    options: CreateLogicNodeFactoryOptions = {}
): LogicGroupNode => ({
    id: nextId('group', options),
    kind: 'group',
    operator: params.operator ?? 'all',
    label: params.label ?? 'Group',
    enabled: true,
    children: params.children ?? [],
    threshold: params.threshold,
    weightThreshold: params.weightThreshold
});

export const createLogicSequence = (
    params: Partial<Pick<LogicSequenceNode, 'label' | 'mode' | 'children' | 'stepTiming' | 'stepCaptures'>> = {},
    options: CreateLogicNodeFactoryOptions = {}
): LogicSequenceNode => ({
    id: nextId('sequence', options),
    kind: 'sequence',
    mode: params.mode ?? 'ordered',
    label: params.label ?? 'Sequence',
    enabled: true,
    children: params.children ?? [],
    stepTiming: params.stepTiming ?? {},
    stepCaptures: params.stepCaptures ?? {}
});

export const createLogicCondition = (
    params: {
        label: string;
        featureRef?: TradeLogicContextFeatureRef | null;
        operator?: TradeLogicComparisonOperator | null;
        value?: TradeLogicJsonValue;
        templateKey?: TradeLogicRegistryKey | null;
        requirementType?: string | null;
        parameters?: TradeLogicJsonObject;
        missingDataPolicy?: TradeLogicMissingDataPolicy | null;
    },
    options: CreateLogicNodeFactoryOptions = {}
): LogicConditionNode => ({
    id: nextId(params.templateKey ? 'condition-template' : 'condition', options),
    kind: 'condition',
    label: params.label,
    enabled: true,
    templateKey: params.templateKey,
    requirementType: params.requirementType,
    featureRef: params.featureRef ?? null,
    operator: params.operator ?? '=',
    value: params.value,
    parameters: params.parameters,
    missingDataPolicy: params.missingDataPolicy
});

export const createLogicAction = (
    params: {
        label: string;
        actionKind: string;
        templateKey?: TradeLogicRegistryKey | null;
        parameters?: TradeLogicJsonObject;
    },
    options: CreateLogicNodeFactoryOptions = {}
): LogicActionNode => ({
    id: nextId('action', options),
    kind: 'action',
    label: params.label,
    enabled: true,
    templateKey: params.templateKey,
    actionKind: params.actionKind,
    parameters: params.parameters ?? {}
});

export const createLogicSchedule = (
    params: {
        label?: string;
        scheduleKind?: LogicScheduleNode['scheduleKind'];
        parameters?: TradeLogicJsonObject;
    } = {},
    options: CreateLogicNodeFactoryOptions = {}
): LogicScheduleNode => ({
    id: nextId('schedule', options),
    kind: 'schedule',
    label: params.label ?? 'Schedule',
    enabled: true,
    scheduleKind: params.scheduleKind ?? 'custom',
    parameters: params.parameters ?? {}
});

export const createLogicReference = (
    params: {
        label: string;
        referenceKind: LogicReferenceKind;
        referenceId: string | number;
        version?: number | null;
        parameters?: TradeLogicJsonObject;
    },
    options: CreateLogicNodeFactoryOptions = {}
): LogicReferenceNode => ({
    id: nextId('reference', options),
    kind: 'reference',
    label: params.label,
    enabled: true,
    referenceKind: params.referenceKind,
    referenceId: params.referenceId,
    version: params.version,
    parameters: params.parameters
});
